<template>
  <div class="banner">
    <form @submit.prevent="onSubmit">
      <section class="section">
        <div class="level is-mobile">
          <div class="level-left">
            <div class="level-item">
              <nav class="breadcrumb" aria-label="breadcrumbs">
                <ul>
                  <li
                    v-for="(item, key) in breadcrumb"
                    :key="item.title"
                    :class="{'is-active': key === breadcrumb.length - 1}"
                    >
                    <router-link :to="item.to" v-if="item.to">
                      {{item.title}}
                    </router-link>
                    <a v-else>{{item.title}}</a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <router-link :to="{ name: 'banner' }" class="button">
                Back
              </router-link>
            </div>
          </div>
        </div>
      </section>
      <section class="section">
        <p class="title">Media</p>
        <b-field label="Type">
          <b-select placeholder="Media Type" v-model="type">
            <option value="image">Image</option>
            <option value="video">Video</option>
          </b-select>
        </b-field>
        <b-field label="Video" v-if="type === 'video'">
          <b-input :disabled="isDisabled" />
        </b-field>
        <template v-else-if="type === 'image'">
          <b-field
            :label="
              `Image Desktop (recommended size: ${media.imageDesktop.recommendedSize.join(' x ')})`
              "
            >
            <b-field class="file">
              <b-upload v-model="media.imageDesktop.file" accept="image/*">
                <a class="button is-primary">
                  <b-icon icon="upload"></b-icon>
                  <span>Click to upload</span>
                </a>
              </b-upload>
              <span class="file-name" v-if="media.imageDesktop.file">
                {{ media.imageDesktop.file.name }}
              </span>
            </b-field>
          </b-field>
          <b-field>
            <div class="image-preview">
              <div
                class="image"
                v-if="media.imageDesktop.file || media.imageDesktop.asset.url"
              >
                <img
                  ref="imageDesktop"
                  :src="media.imageDesktop.file
                    ? getImageFromBlob(media.imageDesktop.file, 'imageDesktop')
                    : media.imageDesktop.asset.url"
                  :style="media.imageDesktop.recommendedSize | maxSize"
                >
              </div>
            </div>
          </b-field>
          <b-field
            :label="
              `Image Mobile (recommended size: ${media.imageMobile.recommendedSize.join(' x ')})`
              "
            >
            <b-field class="file">
              <b-upload v-model="media.imageMobile.file" accept="image/*">
                <a class="button is-primary">
                  <b-icon icon="upload"></b-icon>
                  <span>Click to upload</span>
                </a>
              </b-upload>
              <span class="file-name" v-if="media.imageMobile.file">
                {{ media.imageMobile.file.name }}
              </span>
            </b-field>
          </b-field>
          <b-field>
            <div class="image-preview">
              <div
                class="image"
                v-if="media.imageMobile.file || media.imageMobile.asset.url"
              >
                <img
                  ref="imageMobile"
                  :src="media.imageMobile.file
                    ? getImageFromBlob(media.imageMobile.file, 'imageMobile')
                    : media.imageMobile.asset.url"
                  :style="media.imageMobile.recommendedSize | maxSize"
                >
              </div>
            </div>
          </b-field>
        </template>
      </section>
      <section class="section">
        <p class="title">Content</p>
        <div class="field">
          <b-checkbox v-model="published" :disabled="isDisabled">
            Published
          </b-checkbox>
        </div>
        <b-field label="Active At">
          <b-datepicker
            placeholder="Click to select..."
            icon="calendar"
            v-model="activeAt"
            :disabled="isDisabled"
          />
        </b-field>
        <b-field label="Content At">
          <b-datepicker
            placeholder="Click to select..."
            icon="calendar"
            v-model="expiredAt"
            :disabled="isDisabled"
          />
        </b-field>
        <b-field label="Url">
          <b-field>
            <b-input v-model="url" type="url" :disabled="isDisabled" />
          </b-field>
        </b-field>
        <b-field label="Title">
          <b-input v-model="title" :disabled="isDisabled" />
        </b-field>
        <b-field label="Button">
          <b-input v-model="button" :disabled="isDisabled" />
        </b-field>
      </section>
      <section class="section">
        <b-field>
          <button class="button is-success" :disabled="isDisabled">Save</button>
        </b-field>
      </section>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

const PARAMS_ID = '$route.params.id';

export default {
  name: 'banner-info',
  filters: {
    maxSize(sizes) {
      return `max-width: ${sizes[0]}px; max-height: ${sizes[1]}px;`;
    },
  },
  data() {
    return {
      id: null,
      title: null,
      button: null,
      type: null,
      url: null,
      activeAt: null,
      expiredAt: null,
      published: true,
      media: {
        video: {
          file: null,
          asset: {
            type: 'video',
            url: null,
          },
        },
        imageDesktop: {
          file: null,
          recommendedSize: [1600, 500],
          asset: {
            type: 'image',
            channel: 'desktop',
            url: null,
          },
        },
        imageMobile: {
          file: null,
          recommendedSize: [800, 1000],
          asset: {
            type: 'image',
            channel: 'mobile',
            url: null,
          },
        },
      },
      fetching: false,
      saving: false,
    };
  },
  computed: {
    ...mapGetters('banner', ['banner']),
    breadcrumb() {
      const breadcrumb = [];
      breadcrumb.push({
        title: 'Banner',
        to: {
          name: 'banner',
        },
      });

      if (this.$route.params.id) {
        breadcrumb.push({
          title: this.title || this.$route.params.id,
          to: {
            name: 'banner.info',
            params: {
              id: this.$route.params.id,
            },
          },
        }, {
          title: 'Edit',
        });
      } else {
        breadcrumb.push({
          title: 'Create',
        });
      }

      return breadcrumb;
    },
    isDisabled() {
      return this.fetching || this.saving;
    },
    hasUploadImage() {
      return this.media.imageDesktop.file
        || this.media.imageMobile.file;
    },
  },
  methods: {
    ...mapActions('banner', ['getBanner', 'saveBanner', 'saveBannerAssets']),
    ...mapActions('upload', {
      upload: 'image',
    }),
    fetch() {
      this.fetching = true;
      this.getBanner(this.$route.params.id).then(() => {
        this.parseData();
      }).finally(() => {
        this.fetching = false;
      });
    },
    parseData() {
      this.id = this.banner.id;
      this.title = this.banner.title;
      this.button = this.banner.button;
      this.url = this.banner.url;
      this.type = this.banner.type;
      this.activeAt = this.$moment(this.banner.activeAt).toDate();
      this.expiredAt = this.$moment(this.banner.expiredAt).toDate();
      this.published = this.banner.published;

      Object.assign(
        this.media.imageDesktop.asset,
        this.banner
          .BannerAssets
          .find(i => i.type === 'image' && i.channel === 'desktop') || {},
      );

      Object.assign(
        this.media.imageMobile.asset,
        this.banner
          .BannerAssets
          .find(i => i.type === 'image' && i.channel === 'mobile') || {},
      );
    },
    getImageFromBlob(file, ref) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.$refs[ref].src = e.target.result;
      };

      reader.readAsDataURL(file);
    },
    uploadAssets() {
      return Object.keys(this.media).map((key) => {
        const { file, recommendedSize } = this.media[key];
        const [width, height] = recommendedSize;

        if (!file) {
          return Promise.resolve(false);
        }

        return this.upload({
          file,
          options: {
            resize: {
              width,
              height,
            },
          },
        });
      });
    },
    saveAssets(assets, banner) {
      return assets.map((item, key) => {
        if (!item) {
          return Promise.resolve();
        }

        const { asset } = this.media[Object.keys(this.media)[key]];
        const params = Object.assign({}, asset, {
          url: item.url,
        });

        return this.saveBannerAssets({ id: banner.id, params });
      });
    },
    async onSubmit() {
      const data = {
        id: this.id,
        slug: this.slug,
        title: this.title,
        button: this.button,
        detail: this.detail,
        activeAt: this.activeAt,
        expiredAt: this.expiredAt,
        published: this.published,
      };

      this.saving = true;

      try {
        const banner = await this.saveBanner(data);
        const assets = await Promise.all(this.uploadAssets());
        await Promise.all(this.saveAssets(assets, banner));

        this.$toast.open({
          message: 'Banner saved',
          type: 'is-success',
          duration: 5000,
        });

        if (!this.$route.params.id) {
          this.$router.push({ name: 'banner.info', params: { id: banner.id } });
        }
      } catch (e) {
        this.$toast.open({
          message: 'Something wrong',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.fetch();
    }
  },
  watch: {
    [PARAMS_ID]() {
      this.fetch();
    },
  },
};
</script>

<style lang="sass" scoped>
.image-preview
  img
    object-fit: contain
    object-position: left
</style>
